import React, { useEffect, useRef, useState } from 'react';
import { oneOf, oneOfType, string } from 'prop-types';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { canUseNativeShare, copyToClipboard, listingShareUrl } from '../../util/shareListing';
import { openShareModal } from '../../ducks/favoriteListings.duck';
import { CheckIcon, ShareIcon } from './ListingActionIcons';

import css from './ListingActions.module.css';

export const SHARE_BUTTON_CARD = 'card';
export const SHARE_BUTTON_LABELLED = 'labelled';

// How long the card's button reads "Link copied" after a click.
const COPIED_LABEL_MS = 2200;

/**
 * The Share button, on the listing card and on the listing page.
 *
 * On a phone it opens the system share sheet. Everywhere else — and on a phone
 * whose share sheet fails for any reason other than being dismissed — it copies
 * the listing's link and opens the "Link copied" pop-up, which also shows the
 * link so it can be copied by hand if the clipboard refused.
 *
 * The click is stopped here because on the card this sits inside the card's
 * link. Without it, sharing would also open the listing.
 */
const ShareListingButton = props => {
  const { className, listing, variant } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [showCopied, setShowCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copyAndConfirm = url =>
    copyToClipboard(url).then(copied => {
      dispatch(openShareModal({ url, copied }));
      if (copied) {
        clearTimeout(timer.current);
        setShowCopied(true);
        timer.current = setTimeout(() => setShowCopied(false), COPIED_LABEL_MS);
      }
    });

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();

    const url = listingShareUrl(config.marketplaceRootURL, listing);

    // navigator.share has to be called straight from the click, before any
    // await, or the browser no longer counts it as a user gesture.
    if (canUseNativeShare()) {
      navigator
        .share({ title: listing?.attributes?.title || config.marketplaceName, url })
        .catch(err => {
          // Dismissing the sheet is not a failure.
          if (err?.name !== 'AbortError') {
            copyAndConfirm(url);
          }
        });
      return;
    }

    copyAndConfirm(url);
  };

  const isCard = variant === SHARE_BUTTON_CARD;
  // Only the card's button swaps its label: on the listing page the pop-up is
  // confirmation enough, and the title row should not shift.
  const copiedNow = isCard && showCopied;

  return (
    <button
      type="button"
      className={classNames(isCard ? css.shareCardButton : css.labelledButton, className)}
      onClick={handleClick}
      aria-label={intl.formatMessage({ id: 'ListingActions.shareAriaLabel' })}
    >
      {copiedNow ? (
        <CheckIcon className={classNames(css.cardIcon, css.copiedIcon)} />
      ) : (
        <ShareIcon className={isCard ? css.cardIcon : css.labelledIcon} />
      )}
      <span className={classNames(isCard ? css.cardLabel : css.label, { [css.copiedLabel]: copiedNow })}>
        {intl.formatMessage({
          id: copiedNow ? 'ListingActions.linkCopied' : 'ListingActions.share',
        })}
      </span>
    </button>
  );
};

ShareListingButton.defaultProps = {
  className: null,
  variant: SHARE_BUTTON_CARD,
};

ShareListingButton.propTypes = {
  className: string,
  // An ownListing on the draft and pending-approval variants of the listing page.
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  variant: oneOf([SHARE_BUTTON_CARD, SHARE_BUTTON_LABELLED]),
};

export default ShareListingButton;
